import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Award, ArrowRight } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';

export const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = API_BASE;

  const fetchDoctors = React.useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/doctors/synced`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch doctors');
      }

      const data = await response.json();
      setDoctors(data);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setError('Failed to load doctors. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#14D1A2] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading our medical team...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchDoctors} className="bg-[#14D1A2] hover:bg-[#10B88A]">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">{/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Our Medical Team</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            Meet our experienced doctors committed to your health and wellbeing
          </p>
        </div>
      </section>

      {/* Doctors Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          {doctors.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No doctors available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {doctors.map((doctor) => {
                const profileHref = `/doctors/${doctor.slug || doctor.id}`;
                return (
                  <Card key={doctor.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 group">
                    <Link to={profileHref} data-testid={`doctor-card-${doctor.slug || doctor.id}`}>
                      <div className="aspect-[4/5] overflow-hidden bg-gray-100">
                        <img
                          src={doctor.image}
                          alt={doctor.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x500?text=Doctor+Photo';
                          }}
                        />
                      </div>
                    </Link>
                    <CardContent className="p-6">
                      <Link to={profileHref}>
                        <h3 className="text-2xl font-semibold text-gray-900 mb-2 hover:text-[#14D1A2] transition-colors">{doctor.name}</h3>
                      </Link>
                      <p className="text-[#14D1A2] font-semibold text-lg mb-4">{doctor.specialization}</p>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-start">
                          <GraduationCap className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 text-sm">{doctor.education}</span>
                        </div>
                        <div className="flex items-start">
                          <Award className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 text-sm">{doctor.experience} of experience</span>
                        </div>
                      </div>
                      
                      <p className="text-gray-700 text-sm leading-relaxed mb-6">{doctor.bio}</p>
                      
                      <div className="flex gap-2">
                        <Link to={profileHref} className="flex-1">
                          <Button variant="outline" className="w-full border-[#14D1A2] text-[#14D1A2] hover:bg-[#14D1A2] hover:text-white">
                            View Profile
                          </Button>
                        </Link>
                        <Link to="/book-appointment" className="flex-1">
                          <Button className="w-full bg-[#14D1A2] hover:bg-[#10B88A] text-white">
                            Book
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Why Our Doctors */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Our Doctors</h2>
              <p className="text-lg text-gray-600">
                Excellence, experience, and empathy in every consultation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Extensive Experience</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our doctors bring years of clinical experience and stay updated with the latest medical advancements and treatment protocols.
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Patient-Centered Care</h3>
                <p className="text-gray-700 leading-relaxed">
                  We believe in treating patients, not just symptoms. Every consultation is thorough, personalized, and focused on your wellbeing.
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Clear Communication</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our doctors take time to explain your condition, treatment options, and answer all your questions in simple language.
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Holistic Approach</h3>
                <p className="text-gray-700 leading-relaxed">
                  We focus on preventive care and lifestyle management alongside treatment, ensuring long-term health outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1C5C62] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Book Your Consultation Today</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Choose your preferred doctor and schedule an appointment at your convenience
          </p>
          <Link to="/book-appointment">
            <Button size="lg" className="bg-[#14D1A2] hover:bg-[#10B88A] text-white text-lg px-10 h-14">
              Book Appointment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
